import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Trash2, Loader2, Trophy, Store } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPath, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompareSocial } from "@/components/CompareSocial";
import { VideoPrompts } from "@/components/VideoPrompts";
import { LaunchProduct } from "@/components/LaunchProduct";

const ShopPanel = ({ c, reload }) => {
  const [price, setPrice] = useState(c.price_aud ?? "");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const savePrice = () => run(() => api.put(`/compare/${c.id}/price`, { price_aud: price === "" ? null : Number(price) }), "Price saved");
  const toggle = () => run(() => api.post(`/compare/${c.id}/store/${c.published ? "unpublish" : "publish"}`), c.published ? "Removed from the shop" : "Listed in the shop");
  return (
    <div className="border border-[#E2D9CC] bg-[#FAF7F2] p-6 flex flex-wrap items-end gap-6" data-testid="compare-shop-panel">
      <div className="flex items-center gap-3 mr-auto">
        <Store className="w-5 h-5 text-[#C59B27]" />
        <div>
          <p className="eyebrow">theexpatstudio.com</p>
          <p data-testid="compare-shop-status" className="font-display text-lg text-[#3B1B48]">{c.published ? "For sale in the shop" : "Not in the shop"}</p>
        </div>
      </div>
      <div>
        <label className="text-xs text-[#8C827A] block mb-1">Price (A$) · blank uses default A${c.default_price_aud}</label>
        <div className="flex gap-2">
          <Input data-testid="compare-price-input" type="number" min="1" step="0.5" value={price} placeholder={String(c.default_price_aud)} onChange={(e) => setPrice(e.target.value)} className="rounded-none w-32 h-11" />
          <Button data-testid="compare-price-save" variant="outline" disabled={busy} onClick={savePrice} className="rounded-none h-11">Save</Button>
        </div>
      </div>
      <Button data-testid="compare-publish-toggle" disabled={busy} onClick={toggle} className="rounded-none h-11">{c.published ? "Remove from shop" : "Publish to shop"}</Button>
    </div>
  );
};

const Grid = ({ c }) => {
  const top = (k) => Math.max(0, ...c.profiles.map((p) => p.scores?.[k] || 0));
  return (
    <div className="overflow-x-auto border border-[#E2D9CC]" data-testid="compare-grid">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#3B1B48] text-[#FAF7F2]"><th className="text-left p-4 font-display font-normal">Town</th>{c.criteria.map((k) => <th key={k} className="p-4 font-display font-normal">{c.labels[k]}</th>)}</tr></thead>
        <tbody>
          {c.profiles.map((p) => (
            <tr key={p.town} className="border-b border-[#E2D9CC] bg-[#FAF7F2]">
              <td className="p-4"><span className="font-display text-lg text-[#3B1B48]">{p.town}</span><span className="block text-xs text-[#8C827A]">{[p.province, p.region].filter(Boolean).join(", ")}{p.guide_id ? " · from your guide" : ""}</span></td>
              {c.criteria.map((k) => (
                <td key={k} className="p-4 text-center" data-testid={`score-${p.town}-${k}`}>
                  <span className={`inline-block min-w-[3rem] px-2 py-1 font-display ${p.scores?.[k] && p.scores[k] === top(k) ? "bg-[#C59B27] text-[#1C181D]" : "bg-[#EFE6F3] text-[#3B1B48]"}`}>{p.scores?.[k] ?? "–"}/10</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Details = ({ c }) => (
  <div className="space-y-12">
    {c.criteria.map((k) => (
      <div key={k} data-testid={`compare-section-${k}`}>
        <p className="eyebrow mb-2">{c.labels[k]}</p>
        {c.verdict?.best_for?.[k] && <p className="font-display italic text-[#3B1B48] mb-5">Best: {c.verdict.best_for[k].town} — {c.verdict.best_for[k].why}</p>}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {c.profiles.map((p) => (
            <div key={p.town} className="border-l-2 border-[#C59B27] bg-[#FAF7F2] p-5">
              <p className="font-display text-lg text-[#3B1B48]">{p.town} <span className="text-sm text-[#8C827A]">{p.scores?.[k] ?? "–"}/10</span></p>
              <p className="text-sm mt-2">{p.details?.[k]?.summary}</p>
              <ul className="text-xs text-[#8C827A] mt-2 list-disc pl-4 space-y-1">{(p.details?.[k]?.facts || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function CompareDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/compare/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (c?.status !== "running") return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [c, load]);
  if (!c) return <p className="eyebrow">Loading…</p>;
  const remove = async () => { if (!window.confirm("Delete this comparison?")) return; await api.delete(`/compare/${id}`); nav("/compare"); };

  return (
    <div className="rise space-y-10">
      <Link to="/compare" data-testid="compare-back" className="inline-flex items-center gap-2 text-sm text-[#8C827A] hover:text-[#3B1B48]"><ArrowLeft className="w-4 h-4" />All comparisons</Link>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-3">{c.towns.length ? `${c.towns.length} towns` : c.region} · {c.criteria.length} points</p>
          <h1 data-testid="compare-title" className="font-display text-4xl sm:text-5xl text-[#3B1B48] leading-tight max-w-3xl">{c.title}</h1>
        </div>
        <div className="flex gap-2">
          {c.status === "ready" && <Button data-testid="compare-download" onClick={() => downloadPath(`/compare/${id}/pdf`, "comparison.pdf").catch((e) => toast.error(errMsg(e)))} className="rounded-none h-11"><Download className="w-4 h-4 mr-2" />Download PDF</Button>}
          <Button data-testid="compare-delete" variant="outline" onClick={remove} className="rounded-none h-11"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      {c.status === "running" && (
        <div className="bg-[#3B1B48] text-[#FAF7F2] p-6" data-testid="compare-progress">
          <p className="flex items-center gap-3"><Loader2 className="w-4 h-4 animate-spin text-[#C59B27]" />{c.step} · {c.progress}%</p>
          <div className="h-1.5 bg-[#FAF7F2]/15 mt-4"><div className="h-full bg-[#C59B27] transition-[width] duration-700" style={{ width: `${c.progress}%` }} /></div>
        </div>
      )}
      {c.status === "failed" && <p data-testid="compare-error" className="text-red-700">Stopped: {c.error}</p>}
      {c.status === "ready" && <LaunchProduct path={`/launch/compare/${c.id}`} what="comparison" onChanged={load} />}
      {c.status === "ready" && <ShopPanel c={c} reload={load} />}
      {c.status === "ready" && <CompareSocial c={c} reload={load} />}
      {c.status === "ready" && <VideoPrompts kind="compare" refId={c.id} />}
      {c.verdict?.intro && <p className="max-w-3xl text-[#1C181D]">{c.verdict.intro}</p>}
      {c.profiles.length > 0 && <Grid c={c} />}
      {c.verdict?.pick && <div className="bg-[#EFE6F3] border-l-4 border-[#3B1B48] p-6 flex gap-4" data-testid="compare-pick"><Trophy className="w-6 h-6 text-[#C59B27] shrink-0" /><div><p className="eyebrow">Our pick</p><p className="font-display text-2xl text-[#3B1B48]">{c.verdict.pick.town}</p><p className="text-sm mt-1">{c.verdict.pick.why}</p></div></div>}
      {c.profiles.length > 0 && <Details c={c} />}
      {c.verdict?.overall && <div><p className="eyebrow mb-2">Verdict</p><p className="max-w-3xl">{c.verdict.overall}</p></div>}
    </div>
  );
}
